import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, Calendar, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { getBookingBySession } from "@/lib/booking.functions";
import { useT } from "@/lib/i18n";

const WHATSAPP_NUMBER = "34630072826";

export const Route = createFileRoute("/reserva-confirmada")({
  component: BookingConfirmed,
  validateSearch: (s: Record<string, unknown>) => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
  }),
  head: () => {
    const url = "https://pulso33.com/reserva-confirmada";
    const title = "Booking confirmed — PULSO33";
    const description =
      "Your discovery call with PULSO33 is confirmed. Check the date, time and next steps to prepare for the session.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
});

function BookingConfirmed() {
  const { session_id: sessionId } = Route.useSearch();
  const { tk, lang } = useT();
  const fetchBooking = useServerFn(getBookingBySession);
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await fetchBooking({ data: { sessionId } });
        if (cancelled) return;
        if (r.booking?.status === "paid" || attempts >= 6) {
          setBooking(r.booking);
          setLoading(false);
        } else {
          attempts++;
          setTimeout(poll, 1500);
        }
      } catch {
        setLoading(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, fetchBooking]);

  const locale = lang === "pt" ? "pt-PT" : lang === "en" ? "en-GB" : "es-ES";
  const dateLabel = booking
    ? new Date(booking.booking_date + "T00:00:00").toLocaleDateString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const waMessage = booking
    ? tk("reserva.confirmed.9e8ce4", "Hola, soy ${booking.full_name}. Acabo de reservar la consulta del ${dateLabel} a las ${String(booking.start_time).slice(0, 5)}.")
    : tk("reserva.confirmed.119191", "Hola, acabo de reservar una consulta.");
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`;

  return (
    <Layout>
      <section className="max-w-2xl mx-auto px-6 py-24 text-center">
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-gold" size={32} />
            <p className="text-muted-foreground">{tk("reserva.confirmed.1605f2", "Confirmando tu reserva…")}</p>
          </div>
        ) : booking ? (
          <>
            <CheckCircle2 size={56} className="text-gold mx-auto" strokeWidth={1.2} />
            <h1 className="font-serif text-4xl md:text-5xl mt-6">
              {tk("reserva.confirmed.07da12", "Reserva")} <em className="not-italic gold-gradient">{tk("reserva.confirmed.8b0d05", "confirmada")}</em>
            </h1>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              {tk("reserva.confirmed.138f40", "Te esperamos el")} <strong className="text-foreground">{dateLabel}</strong> {tk("reserva.confirmed.117315", "a las")}{" "}
              <strong className="text-foreground">{String(booking.start_time).slice(0, 5)}</strong>. {tk("reserva.confirmed.ba865d", "Te hemos enviado un email a")} <strong className="text-foreground">{booking.email}</strong> {tk("reserva.confirmed.c2e26d", "con todos los detalles.")}
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-4 text-xs tracking-[0.3em] uppercase hover:opacity-90"
              >
                <MessageCircle size={14} /> {tk("reserva.confirmed.25311a", "Confirmar por WhatsApp")}
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 text-gold py-4 text-xs tracking-[0.3em] uppercase hover:bg-gold hover:text-primary-foreground transition-colors"
              >
                <Calendar size={14} /> {tk("reserva.confirmed.1afb11", "Volver al inicio")}
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl">{tk("reserva.confirmed.946206", "No encontramos tu reserva")}</h1>
            <p className="mt-4 text-muted-foreground">
              {tk("reserva.confirmed.3d89b1", "Si el cargo se ha realizado, escríbenos y lo resolveremos al momento.")}
            </p>
            <Link
              to="/contact"
              className="inline-flex mt-8 rounded-full bg-gold text-primary-foreground px-8 py-3 text-xs tracking-[0.3em] uppercase"
            >
              {tk("reserva.confirmed.349d77", "Volver a reservar")}
            </Link>
          </>
        )}
      </section>
    </Layout>
  );

}
